// @warp-css;
import { i18n } from "@lingui/core";
import { interleave } from "@warp-ds/core/breadcrumbs";
import { html, LitElement } from "lit";
import { activateI18n } from "../i18n";
import { reset } from "../styles.js";
import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
import { styles } from "./styles.js";
const ccBreadcrumbs = {
    wrapper: "flex space-x-8",
    text: "s-text",
    link: "s-text-link",
    separator: "select-none s-icon",
    a11y: "sr-only",
};
const separator = html `<span class=${ccBreadcrumbs.separator}>/</span>`;
/**
 * Shows the navigation structure for the current page.
 * Renders direct child links and non-link elements as a breadcrumb trail, inserts separators between items, and exposes the trail as navigation.
 *
 * [See Storybook for usage examples](https://warp-ds.github.io/elements/?path=/docs/navigation-breadcrumbs--docs)
 */
class WarpBreadcrumbs extends LitElement {
    static { this.styles = [reset, styles]; }
    constructor() {
        super();
        this._children = [];
        this._internals = this.attachInternals();
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
        // Store default label for use in render
        this._defaultLabel = i18n._({
            id: "breadcrumbs.ariaLabel",
            message: "You are here",
            comment: "Default screen reader message for the breadcrumb component",
        });
        // Use ElementInternals for ARIA to avoid hydration mismatches
        this._internals.ariaLabel = this._defaultLabel;
    }
    get _label() {
        return this.ariaLabel ?? this._defaultLabel;
    }
    connectedCallback() {
        super.connectedCallback();
        // Use ElementInternals for ARIA to avoid hydration mismatches
        this._internals.role = "navigation";
        const anchors = this.querySelectorAll(":scope > a");
        const spans = this.querySelectorAll(":scope > span");
        const breadcrumbItems = this.querySelectorAll("w-breadcrumb-item");
        // Warn if using the Legacy API and Item API together.
        if (breadcrumbItems.length > 0 &&
            (anchors.length > 0 || spans.length > 0)) {
            console.warn("Mixing Legacy API and w-breadcrumb-item API children is not supported.");
        }
        let numCurrentPageAttrs = 0;
        let currentPageAttrOnNonFinalItem = false;
        breadcrumbItems.forEach((item, index) => {
            if (item.hasAttribute("current-page")) {
                if (index < breadcrumbItems.length - 1) {
                    currentPageAttrOnNonFinalItem = true;
                }
                numCurrentPageAttrs++;
            }
        });
        if (numCurrentPageAttrs > 1) {
            console.warn("Please ensure only one w-breadcrumb-item has the current-page attribute.");
        }
        if (currentPageAttrOnNonFinalItem) {
            console.warn("The current-page attribute should only be used on the final breadcrumb item.");
        }
        // Grab existing children at the point that the component is added to the page
        const flattenedChildren = [...this.children]
            .filter((child) => {
            return child && !(child.tagName === "W-BREADCRUMB-ITEM");
        })
            .flat(Number.POSITIVE_INFINITY)
            // Filter out empty text nodes and also any w-breadcrumb-item elements
            .filter((child) => child);
        const styledChildren = flattenedChildren.map((child, index) => {
            if (typeof child === "string") {
                const isLastEl = index === this.children.length - 1;
                return html `<span
					class=${ccBreadcrumbs.text}
					aria-current=${isLastEl ? "page" : undefined}
					>${child}</span
				>`;
            }
            child.classList.add(child.tagName === "A" ? ccBreadcrumbs.link : ccBreadcrumbs.text);
            return child;
        });
        // Interleave '/' separator with breadcrumbs
        this._children = interleave(styledChildren, separator);
    }
    render() {
        return html `
			<nav aria-labelledby="breadCrumbLabel">
				<h2 id="breadCrumbLabel" class=${ccBreadcrumbs.a11y}>${this._label}</h2>
				<div class=${ccBreadcrumbs.wrapper}>${this._children}<slot></slot></div>
			</nav>
		`;
    }
}
if (!customElements.get("w-breadcrumbs")) {
    customElements.define("w-breadcrumbs", WarpBreadcrumbs);
}
export { WarpBreadcrumbs };
//# sourceMappingURL=breadcrumbs.js.map